/**
 * CalendarBanner - styled components
 * Light/Dark via data-mode on Container; children use [data-mode='dark'] &
 */

import Icon from 'antd/lib/icon'
import styled from 'styled-components'

export type CalendarBannerMode = 'light' | 'dark'

/** Full-bleed shell: background / borders span 100% width */
export const Container = styled.div<{ order?: number }>`
  box-sizing: border-box;
  width: 100%;
  border-top: var(--border-regular) solid var(--neutral-border-light);
  border-bottom: var(--border-regular) solid var(--neutral-border-light);
  background: var(--neutral-fill-lightest);
  order: ${({ order }) => order || 0};
  &[data-mode='dark'] {
    background: var(--neutral-darkest);
  }
`

/** Centered content lane capped at desktop max width */
export const Inner = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-16);
  padding: 16px var(--spacing-72);
  @media (max-width: 1024px) {
    flex-wrap: wrap;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-16);
    padding: var(--spacing-24);
    &[data-collapsible='true'] {
      transition: max-height .25s;
      max-height: 280px;
      overflow: hidden;
      flex-wrap: nowrap;
      &[data-collapsed='true'] {
        max-height: 40px;
        padding-block: var(--spacing-6);
      }
    }
  }
`

export const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-4);
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    align-items: stretch;
  }
`

export const Headline = styled.h2`
  margin: 0;
  font: 600 32px/38px var(--font-barlow-semi-condensed);
  text-transform: uppercase;
  color: var(--neutral-darkest);
  white-space: nowrap;
  [data-mode='dark'] & {
    color: var(--neutral-white);
  }
  @media (max-width: 1279px) {
    font: 600 24px/30px var(--font-barlow-semi-condensed);
    letter-spacing: 0.4px;
  }
  @media (max-width: 768px) {
    white-space: normal;
    text-align: center;
    width: 100%;
    transition: font-size .15s;
    &[data-collapsed='true'] {
      font-size: 14px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      span {
        margin-left: 4px;
      }
    }
  }
`

export const HeadlineDate = styled.span`
  color: var(--accent-red);
  @media (max-width: 768px) {
    display: block;
  }
`

export const Disclaimer = styled.p`
  margin: 0;
  font: 400 12px/20px var(--font-inter);
  color: var(--neutral-muted);
  @media (max-width: 1279px) {
    display: none;
  }
`

export const DisclaimerTablet = styled.div`
  display: none;
  font: 400 12px/20px var(--font-inter);
  color: var(--neutral-muted);
  @media (min-width: 769px) and (max-width: 1279px) {
    display: block;
  }
  p {
    margin: 0;
    line-height: 20px;
  }
`

export const DisclaimerMobile = styled.p`
  display: none;
  margin: 0;
  width: 100%;
  font: 400 12px/20px var(--font-inter);
  color: var(--neutral-muted);
  text-align: center;
  @media (max-width: 768px) {
    display: block;
  }
`

export const RightTimeline = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-16);
  flex-shrink: 0;
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    gap: 8px;
  }
`

export const DateBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-4);
  white-space: nowrap;
  @media (max-width: 768px) {
    width: 64px;
    gap: 2px;
  }
`

export const DateLabel = styled.span`
  font: 600 12px/18px var(--font-barlow);
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
  color: var(--neutral-secondary);
  [data-mode='dark'] & {
    color: var(--neutral-muted);
  }
  @media (max-width: 768px) {
    display: block;
    width: max-content;
    max-width: none;
    min-width: 0;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  }
`

export const DateValue = styled.span`
  font: 600 24px/30px var(--font-barlow-semi-condensed);
  letter-spacing: 0.4px;
  text-transform: capitalize;
  color: var(--neutral-darkest);
  [data-mode='dark'] & {
    color: var(--neutral-white);
  }
  @media (max-width: 768px) {
    font: 600 20px/26px var(--font-barlow-semi-condensed);
    letter-spacing: 0.6px;
  }
`

export const TransitContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);
  width: 220px;
  height: 52px;
  @media (max-width: 768px) {
    flex: 1;
    width: auto;
    height: auto;
    min-width: 0;
  }
`

export const DaysBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding-inline: var(--spacing-8);
  border-radius: var(--spacing-4);
  background: var(--neutral-darkest);
  font: 600 12px/20px var(--font-barlow);
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--neutral-white);
  white-space: nowrap;
  [data-mode='dark'] & {
    background: var(--neutral-lightest-block);
    color: var(--neutral-darkest);
  }
`

export const TransitBar = styled.div`
  width: 100%;
  height: 4px;
  background: linear-gradient(to right, var(--neutral-darkest), #e31937);
  [data-mode='dark'] & {
    background: linear-gradient(
      to right,
      var(--neutral-lightest-block),
      #e31937
    );
  }
  @media (max-width: 768px) {
    max-width: 220px;
    [data-mode='dark'] & {
      height: 2px;
    }
  }
`

export const PeakSeasonLabel = styled.span`
  font: 500 10px/14px var(--font-barlow);
  letter-spacing: 0.2px;
  text-align: center;
  color: var(--neutral-secondary);
  [data-mode='dark'] & {
    color: var(--neutral-lightest-block);
  }
  @media (max-width: 768px) {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const ExpandedButton = styled(Icon)`
  align-self: center;
  font-size: 18px;
  margin-bottom: -8px;
  color: var(--neutral-darkest);
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`

export const CollapsedBtn = styled(Icon)`
  display: none;
  position: absolute;
  right: 16px;
  font-size: 14px;
  color: var(--neutral-darkest);
  @media (max-width: 768px) {
    display: block;
  }
`



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/components/CalendarBanner/styledComponents.tsx